/**
 * Camera acquisition.
 *
 * We call `getUserMedia` EXACTLY ONCE for the whole app lifetime and share the
 * resulting stream/track everywhere. This is not just tidy — on iOS Safari a
 * second `getUserMedia` on the same physical camera is dangerous: older
 * versions permanently mute the *first* captured track when a second capture
 * starts. Online mode (Phase 3) requests the mic in that SAME single call
 * (`audio: true` below) — the video track feeds MediaPipe, and the whole
 * stream (video + audio tracks) is handed to `RTCPeerConnection.addTrack`
 * directly; solo mode never asks for audio at all.
 */

export interface StartCameraOptions {
  /** Request a mic track in the same getUserMedia call (online mode only). */
  audio?: boolean;
}

export async function startCamera(
  opts: StartCameraOptions = {},
): Promise<{ video: HTMLVideoElement; stream: MediaStream }> {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: {
      facingMode: "user",
      width: { ideal: 640 },
      height: { ideal: 480 },
      frameRate: { ideal: 30, max: 30 },
    },
    audio: opts.audio ? { echoCancellation: true, noiseSuppression: true } : false,
  });

  const video = document.createElement("video");
  video.playsInline = true;
  video.muted = true;
  video.autoplay = true;
  // The attribute form is required on iOS in addition to the property.
  video.setAttribute("playsinline", "");
  video.setAttribute("muted", "");
  video.srcObject = stream;

  await video.play();

  // iOS reports 0×0 until the first frame is decoded. Poll until real
  // dimensions arrive; never trust the requested 640×480 (portrait swaps them).
  await waitForDimensions(video);

  return { video, stream };
}

/**
 * Poll until a `<video>` element reports real decoded dimensions. Exported so
 * a SECOND video element fed from a different source (Phase 4's penalty
 * stage, against the remote WebRTC stream) can wait for real frames the same
 * way before handing it to `createFaceTracker` — starting detection on a 0×0
 * video makes the GPU self-test fail for the wrong reason.
 */
export function waitForDimensions(video: HTMLVideoElement): Promise<void> {
  return new Promise<void>((resolve) => {
    if (video.videoWidth > 0 && video.videoHeight > 0) {
      resolve();
      return;
    }
    const iv = window.setInterval(() => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        window.clearInterval(iv);
        resolve();
      }
    }, 50);
  });
}

export function stopCamera(stream: MediaStream): void {
  for (const track of stream.getTracks()) track.stop();
}
